import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  Checkbox,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useUserContext } from "../Context/UserContext";
import UserHeader from "./UserHeader";
import UserSideBar from "./UserSideBar";
import { Tooltip } from "@mui/material";
import {
  containerStyle,
  paperStyle,
  disabledTextStyle,
  titleStyle,
} from "../styles/UserStyle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { fileIcons, defaultIcon } from "../utils/fileExtensions";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ContentCutRoundedIcon from "@mui/icons-material/ContentCutRounded";
import CompressIcon from "@mui/icons-material/Compress";
import { toast } from "react-toastify";
import ToolbarSection from "./ToolbarSelection";
import api, { fetchFolderContents, getSharedFoldersByPath } from "../api"; // ajuste o caminho se necessário

const menuUserWhiteTheme = "#d4eaff";
const menuUserDarkTheme = "#626262";
const darkTextColor = "#e0f6ff";

interface Item {
  path: string;
  isDirectory: boolean;
  modifiedDate?: string;
  size?: string | null; // <-- Aqui está o ajuste
  accessPermission?: boolean;
}

interface FolderNode {
  name: string;
  path: string;
  subfolders: FolderNode[];
  accessPermission?: boolean;
}

const User: React.FC = () => {
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const { currentPath, setCurrentPath } = useUserContext();
  const [mode, setMode] = useState<"light" | "dark">("light");
  const [fullName, setFullName] = useState<string>("Usuário");
  const [login, setLogin] = useState<string>("");
  const [items, setItems] = useState<Item[]>([]);
  const [rootFolder, setRootFolder] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [renameItemPath, setRenameItemPath] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>("");
  const isAllSelected = selectedItems.size === items.length && items.length > 0;
  const [hoverMessage, setHoverMessage] = useState<string | null>(null);
  const [userType, setUserType] = useState<number | null>(null);
  const { breadcrumb, setBreadcrumb } = useUserContext();
  const [hoverStatusMessage, setHoverStatusMessage] = useState<string | null>(
    null
  );
  const { updatePathAndBreadcrumb } = useUserContext();
  const [foldersTree, setFoldersTree] = useState<FolderNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const isAtRootFolder = currentPath === rootFolder;
  const firstFolderClicked = useRef(false);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setFullName(user.fullName || "Usuário");
      setLogin(user.login || "");
      setUserType(user.type || null);

      // 🔁 Resetar breadcrumb e currentPath na troca de usuário
      setCurrentPath("");
      setBreadcrumb([{ label: "Área de Trabalho", path: "" }]);
    }
  }, []);

  useEffect(() => {}, [breadcrumb]);

  useEffect(() => {
    return () => {
      // Quando o componente for desmontado, redefine o caminho atual
      setCurrentPath("");
      setItems([]); // Opcional: limpa os itens exibidos
    };
  }, [setCurrentPath]);

  const toggleColorMode = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: "#1976d2" },
          background: { default: mode === "dark" ? "#545454" : "#cfe3fc" },
          text: {
            primary: mode === "dark" ? "#e0f6ff" : "#626262", // Cor consistente para texto
          },
        },
      }),
    [mode]
  );

  useEffect(() => {
    if (isLoaded) {
      document.body.style.transition = "background-color 0.8s ease-in-out";
    } else {
      document.body.style.transition = "none";
    }
    document.body.style.backgroundColor = theme.palette.background.default;

    if (!isLoaded) {
      setIsLoaded(true);
    }
  }, [theme.palette.background.default, isLoaded]);

  const fetchFolders = async (
    path: string = currentPath,
    newItems?: Item[]
  ): Promise<FolderNode[]> => {
    // Caso haja novos itens sendo passados (por exemplo, após uma criação de pasta)
    if (newItems) {
      const filteredItems = newItems
        .filter((item) => {
          if (typeof item.path !== "string") return false;

          // Se for uma pasta, verifica se ela tem permissão
          if (item.isDirectory) {
            return item.accessPermission === true;
          }

          // Se for um arquivo, exibe apenas se estiver dentro de uma pasta com permissão
          const parentPath = item.path.substring(
            0,
            item.path.lastIndexOf("\\")
          );
          const parentItem = newItems.find(
            (parent) => parent.path === parentPath && parent.isDirectory
          );

          return parentItem?.accessPermission === true;
        })
        .sort((a, b) => {
          if (a.isDirectory && !b.isDirectory) return -1;
          if (!a.isDirectory && b.isDirectory) return 1;
          return a.path.localeCompare(b.path);
        });

      setItems(filteredItems);
      return []; // <- Nenhuma árvore retornada neste caso
    }

    const storedItemsRaw: Item[] = JSON.parse(
      sessionStorage.getItem("userFolders") || "[]"
    );

    const uniqueItems: Item[] = Array.from(
      new Map(
        storedItemsRaw.map((item) => [
          item.path,
          typeof item.path === "string" && typeof item.isDirectory === "boolean"
            ? item
            : null,
        ])
      ).values()
    ).filter(Boolean) as Item[];

    if (uniqueItems.length > 0) {
      // Função auxiliar para construir a árvore
      const buildTree = (items: Item[]): FolderNode[] => {
        const directories = items.filter((item) => item.isDirectory);

        const buildHierarchy = (parentPath: string): FolderNode[] => {
          return directories
            .filter(
              (dir) =>
                dir.path.startsWith(parentPath + "\\") &&
                dir.path.replace(parentPath + "\\", "").split("\\").length === 1
            )
            .map((dir) => ({
              name: dir.path.split("\\").pop() || "",
              path: dir.path,
              subfolders: buildHierarchy(dir.path),
              accessPermission: dir.accessPermission,
            }));
        };

        const rootFolders = directories.filter((dir) => {
          return !directories.some(
            (otherDir) =>
              otherDir.path !== dir.path &&
              dir.path.startsWith(otherDir.path + "\\")
          );
        });

        return rootFolders.map((root) => ({
          name: root.path.split("\\").pop() || "",
          path: root.path,
          subfolders: buildHierarchy(root.path),
          accessPermission: root.accessPermission,
        }));
      };

      const accessibleItems = uniqueItems.filter((item) => {
        if (item.isDirectory) {
          return item.accessPermission !== false;
        }

        const parentPath = item.path.substring(0, item.path.lastIndexOf("\\"));
        const parentItem = uniqueItems.find(
          (parent) => parent.path === parentPath && parent.isDirectory
        );
        return parentItem?.accessPermission !== false;
      });

      const tree = buildTree(accessibleItems);
      setFoldersTree(tree);

      const topLevelItems = accessibleItems.filter((item) => {
        if (!path) {
          // Se o caminho atual for raiz (""), traga apenas as pastas/arquivos raiz
          return !accessibleItems.some(
            (other) =>
              other.path !== item.path &&
              item.path.startsWith(other.path + "\\")
          );
        }

        // Itens diretamente dentro da pasta atual (um nível abaixo)
        if (item.path.startsWith(path + "\\") || item.path === path) {
          const relativePath = item.path.replace(path + "\\", "");
          return !relativePath.includes("\\");
        }

        return false;
      });

      const sortedItems = topLevelItems.sort((a, b) => {
        if (a.isDirectory && !b.isDirectory) return -1;
        if (!a.isDirectory && b.isDirectory) return 1;
        return a.path.localeCompare(b.path);
      });

      setItems(sortedItems);
      return tree; // <- Retorna a árvore construída
    } else {
      setFoldersTree([]);
      setItems([]);
      return []; // <- Retorna árvore vazia
    }
  };

  const addStatusMessage = (message: string) => {
    setStatusMessage((prev) => {
      const messages = prev ? prev.split(" | ") : [];
      if (messages.includes(message)) return prev; // Evita duplicatas
      return [...messages, message].join(" | ");
    });
  };

  const removeStatusMessage = (message: string) => {
    setStatusMessage((prev) => {
      const messages = prev ? prev.split(" | ") : [];
      const updatedMessages = messages.filter((msg) => msg !== message);
      return updatedMessages.length > 0 ? updatedMessages.join(" | ") : "";
    });
  };

  useEffect(() => {
    const initialize = async () => {
      let foldersRaw = sessionStorage.getItem("userFolders");

      if (!foldersRaw || JSON.parse(foldersRaw).length === 0) {
        const user = JSON.parse(sessionStorage.getItem("user") || "{}");
        if (user?.login) {
          const fetched = await getSharedFoldersByPath({
            path: "", // ou alguma pasta inicial se quiser restringir
            username: user.login,
          });

          const fixed = fetched.map((item) => ({
            ...item,
            path: item.path.replace(/\\\\/g, "\\"),
          }));
          sessionStorage.setItem("userFolders", JSON.stringify(fixed));
        }
      }

      const updatedTree = await fetchFolders("");
      setIsFirstVisit(false);
    };

    if (isFirstVisit) {
      initialize();
    }
  }, [isFirstVisit]);

  useEffect(() => {
    const selectedCount = selectedItems.size;
    if (selectedCount === 1) {
      setStatusMessage("1 item selecionado");
    } else if (selectedCount > 1) {
      setStatusMessage(`${selectedCount} itens selecionados`);
    } else {
      setStatusMessage("");
    }
  }, [selectedItems]);

  useEffect(() => {
    const handleCopyTrigger = () => {
      if (selectedItems.size > 0) {
        const sourcePath = Array.from(selectedItems)[0]; // só o primeiro
        // Garante seleção antes do trigger
        setSelectedItems(new Set([sourcePath]));
        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent("triggerCopyDialog", { detail: sourcePath })
          );
        }, 0);
      } else {
        toast.error("Selecione pelo menos um item para copiar.");
      }
    };

    document.addEventListener("triggerCopy", handleCopyTrigger);
    return () => {
      document.removeEventListener("triggerCopy", handleCopyTrigger);
    };
  }, [selectedItems]);

  useEffect(() => {
    const handleMoveTrigger = () => {
      if (selectedItems.size === 1) {
        const sourcePath = Array.from(selectedItems)[0];
        // Garante seleção antes de disparar o evento
        setSelectedItems(new Set([sourcePath]));
        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent("triggerMoveDialog", { detail: sourcePath })
          );
        }, 0);
      } else {
        toast.error("Selecione apenas um item para mover.");
      }
    };

    document.addEventListener("triggerMove", handleMoveTrigger);
    return () => {
      document.removeEventListener("triggerMove", handleMoveTrigger);
    };
  }, [selectedItems]);

  const handleDownloadClick = (filePath: string) => {
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      updated.add(filePath);
      return updated;
    });
    document.dispatchEvent(
      new CustomEvent("triggerDownload", { detail: filePath })
    );
  };

  useEffect(() => {
    const initializeRootPath = async () => {
      await fetchFolders();
      if (!currentPath) {
        setCurrentPath(rootFolder);
      }
    };

    initializeRootPath();

    const handleFolderCreated = () => {
      fetchFolders(currentPath);
    };

    window.addEventListener("folderCreated", handleFolderCreated);
    return () => {
      window.removeEventListener("folderCreated", handleFolderCreated);
    };
  }, [rootFolder, currentPath, setCurrentPath]);

  useEffect(() => {
    setSelectedItems(new Set());
  }, [currentPath]);

  useEffect(() => {
    const handleSidebarClick = async (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      const clickedPath = customEvent.detail;

      if (typeof clickedPath === "string") {
        // ✅ Limpa breadcrumb e flag de clique inicial
        setBreadcrumb([]);
        firstFolderClicked.current = false;

        // ✅ Chama o handler que vai montar o novo breadcrumb
        await handleFolderClick(clickedPath);
      }
    };

    document.addEventListener("sidebarFolderClicked", handleSidebarClick);
    return () => {
      document.removeEventListener("sidebarFolderClicked", handleSidebarClick);
    };
  }, [foldersTree, login]);

  useEffect(() => {
    if (!isFirstVisit && currentPath !== "") {
      fetchFolders(currentPath); // ← Atualiza visual com base no novo path
    }
  }, [currentPath]);

  const getLastFolder = (path: string): string => {
    const lastIndex = path.lastIndexOf("\\");
    return path.substring(lastIndex + 1);
  };

  // Obtém o caminho da pasta pai
  const getParentFolder = (path: string): string => {
    const lastIndex = path.lastIndexOf("\\");
    return path.substring(0, lastIndex);
  };

  const handleFolderClick = async (path: string) => {
    setCurrentPath(path);
    setIsLoading(true);

    updatePathAndBreadcrumb(path, foldersTree);
    firstFolderClicked.current = true;

    const folderContents = await fetchFolderContents(path, login);
    setItems(folderContents);
    setIsLoading(false);
  };

  const renderBreadcrumbs = () => {
    return (
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        {breadcrumb.map((part, index) => {
          const isLast = index === breadcrumb.length - 1;

          return isLast ? (
            <Typography key={index}>{part.label}</Typography>
          ) : (
            <Link
              key={index}
              onClick={() => handleBreadcrumbClick(part.path, index)}
              sx={{
                cursor: "pointer",
                "&:hover": { color: "#1976d2" },
              }}
            >
              {part.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  // Handle para clique nos breadcrumbs
  const handleBreadcrumbClick = async (path: string, index: number) => {
    setBreadcrumb((prevBreadcrumb) => prevBreadcrumb.slice(0, index + 1));
    setCurrentPath(path);
    setIsLoading(true);
    const folderContents = await fetchFolderContents(path, login);
    setItems(folderContents);

    setIsLoading(false);
  };

  const renderIcon = (item: Item) => {
    const iconColor = mode === "dark" ? "#e0f6ff" : "#626262";

    if (item.isDirectory) {
      return <FolderIcon sx={{ color: iconColor }} />;
    }

    const fileExtension = item.path.split(".").pop()?.toLowerCase() || "";
    const IconComponent = fileIcons[`.${fileExtension}`] || defaultIcon;

    return IconComponent({ sx: { color: iconColor, fontSize: 24 } });
  };

  const clearSelectedItems = () => {
    setSelectedItems(new Set());
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", height: "100vh", zIndex: 0 }}>
        <UserSideBar />
        <Box sx={{ flexGrow: 1 }}>
          <UserHeader
            toggleColorMode={toggleColorMode}
            fullName={fullName}
            login={login}
            userType={userType ?? 1} // Define 1 como valor padrão caso seja null
          />
          <Box sx={containerStyle}>
            <Paper sx={paperStyle}>
              <Typography variant="h4" sx={titleStyle} gutterBottom>
                Explorador de Arquivos
              </Typography>

              <ToolbarSection
                mode={mode}
                menuUserDarkTheme={menuUserDarkTheme}
                menuUserWhiteTheme={menuUserWhiteTheme}
                theme={theme}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                isAllSelected={isAllSelected}
                items={items}
                clearSelectedItems={clearSelectedItems}
                addStatusMessage={addStatusMessage}
                setHoverStatusMessage={setHoverStatusMessage}
                currentPath={currentPath}
                rootFolder={rootFolder}
                renameItemPath={renameItemPath}
                setRenameItemPath={setRenameItemPath}
                toggleColorMode={toggleColorMode}
                fetchFolders={async (path) => {
                  await fetchFolders(path ?? currentPath); // Ignora o retorno
                }}
              />

              <Box sx={{ mb: 2, width: "100%" }}>{renderBreadcrumbs()}</Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2.5fr 2fr 1fr 2fr", // Ajustando as colunas para espaçamento adequado
                  gap: 1,
                  padding: "8px 0",
                  backgroundColor: mode === "dark" ? "#3c3c3c" : "#d4f4ff",
                  borderRadius: 1,
                  transition: "background-color 0.8s ease-in-out",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: 4,
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={
                      selectedItems.size === items.length && items.length > 0
                    }
                    indeterminate={
                      selectedItems.size > 0 &&
                      selectedItems.size < items.length
                    }
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      padding: 0, // Remover espaçamento interno
                      marginRight: 1, // Adicionar espaçamento consistente com os checkboxes na lista
                    }}
                    onChange={(event) => {
                      if (event.target.checked) {
                        // Selecionar todos os itens
                        const allItems = new Set(
                          items.map((item) => item.path)
                        );
                        setSelectedItems(allItems);
                      } else {
                        // Desmarcar todos os itens
                        setSelectedItems(new Set());
                      }
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      textAlign: "left",
                    }}
                  >
                    Nome
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    textAlign: "left",
                    paddingLeft: 11.5,
                    borderLeft: "1px solid #ccc", // Barrinha à direita
                  }}
                >
                  Data de Modificação
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    textAlign: "left",
                    paddingLeft: 6,
                    borderLeft: "1px solid #ccc", // Barrinha à direita
                    borderRight: "1px solid #ccc", // Barrinha à direita
                  }}
                >
                  Tamanho
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  Ações
                </Typography>
              </Box>

              <List sx={{ maxHeight: 550, overflowY: "auto", paddingLeft: 2 }}>
                {items.map((item, index) => {
                  if (typeof item.path !== "string") {
                    console.error(`Item inválido no índice ${index}:`, item);
                    return null;
                  }

                  const itemName = item.path.split("\\").pop() || ""; // Extrai apenas o nome do arquivo ou pasta
                  const isSelected = selectedItems.has(item.path);

                  return (
                    <ListItem
                      key={index}
                      disablePadding
                      onMouseEnter={() => {
                        setHoveredItem(item.path); // Atualiza o item que está com o hover ativo
                      }}
                      onMouseLeave={() => {
                        setHoveredItem(null); // Remove o hover quando o mouse sai do item
                      }}
                      sx={{
                        padding: "0",
                        backgroundColor:
                          hoveredItem === item.path
                            ? "rgba(0, 0, 0, 0.05)"
                            : "transparent", // Destaque no hover
                        marginBottom: "-6px",
                        overflow: "hidden",
                      }}
                    >
                      <Grid
                        container
                        spacing={1}
                        sx={{ alignItems: "center", width: "100%" }}
                      >
                        {/* Nome do Item */}
                        <Grid item xs={4}>
                          <ListItemButton
                            onDoubleClick={() => {
                              if (item.isDirectory)
                                handleFolderClick(item.path); // Ação ao dar duplo clique
                            }}
                            sx={{
                              padding: "2px 8px",
                              color:
                                mode === "dark"
                                  ? darkTextColor
                                  : menuUserDarkTheme,
                              "&:hover": { backgroundColor: "transparent" },
                              textAlign: "left",
                              height: "32px",
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                minWidth: 0,
                                marginRight: 1,
                              }}
                            >
                              <Checkbox
                                edge="start"
                                checked={selectedItems.has(item.path)} // Verifica se o item está selecionado
                                tabIndex={-1}
                                disableRipple
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelectedItems((prev) => {
                                    const updated = new Set(prev);
                                    if (updated.has(item.path)) {
                                      updated.delete(item.path);
                                    } else {
                                      updated.add(item.path);
                                    }
                                    return updated;
                                  });
                                }}
                              />
                              {renderIcon(item)}
                            </ListItemIcon>
                            <ListItemText
                              primary={itemName}
                              className="item-text"
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            />
                          </ListItemButton>
                        </Grid>

                        {/* Data de Modificação */}
                        <Grid item xs={3.2}>
                          <Typography
                            sx={{ textAlign: "center", whiteSpace: "nowrap" }}
                          >
                            {item.modifiedDate || "N/A"}
                          </Typography>
                        </Grid>

                        {/* Tamanho */}
                        <Grid item xs={1.8}>
                          <Typography
                            sx={{
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              paddingRight: item.size ? "20px" : "85px",
                            }}
                          >
                            {item.size ? (
                              <>
                                <span>{item.size?.split(" ")[0]}</span>{" "}
                                <span>{item.size?.split(" ")[1]}</span>
                              </>
                            ) : (
                              "-" // Exibe traço se não houver tamanho
                            )}
                          </Typography>
                        </Grid>

                        {/* Ações */}
                        <Grid item xs={3}>
                          <Box
                            sx={{
                              pr: 2, // Espaçamento à direita
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end", // Alinha os ícones à direita
                              visibility:
                                hoveredItem === item.path
                                  ? "visible"
                                  : "hidden", // Mostra as ações apenas no hover
                            }}
                          >
                            {/* Ícones de ações */}
                            {!item.isDirectory && (
                              <Tooltip
                                title={
                                  isAtRootFolder
                                    ? "Ação não permitida na Área de Trabalho."
                                    : "Baixar (Download)"
                                }
                                arrow
                              >
                                <span
                                  style={{
                                    cursor: isAtRootFolder
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                >
                                  <CloudDownloadIcon
                                    sx={{
                                      color:
                                        mode === "dark"
                                          ? darkTextColor
                                          : menuUserDarkTheme,
                                      "&:hover": {
                                        color: isAtRootFolder
                                          ? "inherit"
                                          : "#1976d2",
                                      },
                                      pointerEvents: isAtRootFolder
                                        ? "none"
                                        : "auto",
                                      opacity: isAtRootFolder ? 0.5 : 1,
                                    }}
                                    onClick={() =>
                                      !isAtRootFolder &&
                                      handleDownloadClick(item.path)
                                    }
                                  />
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip
                              title={
                                isAtRootFolder
                                  ? "Ação não permitida na Área de Trabalho."
                                  : "Compactar"
                              }
                              arrow
                            >
                              <span
                                style={{
                                  cursor: isAtRootFolder
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                <CompressIcon
                                  sx={{
                                    color:
                                      mode === "dark"
                                        ? darkTextColor
                                        : menuUserDarkTheme,
                                    "&:hover": {
                                      color: isAtRootFolder
                                        ? "inherit"
                                        : "#1976d2",
                                    },
                                    pointerEvents: isAtRootFolder
                                      ? "none"
                                      : "auto",
                                    opacity: isAtRootFolder ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isAtRootFolder) {
                                      const itemName =
                                        item.path.split("\\").pop() || "";
                                      document.dispatchEvent(
                                        new CustomEvent("triggerZipDialog", {
                                          detail: {
                                            itemPath: item.path,
                                            itemName: itemName,
                                          },
                                        })
                                      );
                                    }
                                  }}
                                />
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                isAtRootFolder
                                  ? "Ação não permitida na Área de Trabalho."
                                  : "Renomear"
                              }
                              arrow
                            >
                              <span
                                style={{
                                  cursor: isAtRootFolder
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                <ModeEditOutlinedIcon
                                  sx={{
                                    color:
                                      mode === "dark"
                                        ? darkTextColor
                                        : menuUserDarkTheme,
                                    "&:hover": {
                                      color: isAtRootFolder
                                        ? "inherit"
                                        : "#1976d2",
                                    },
                                    pointerEvents: isAtRootFolder
                                      ? "none"
                                      : "auto",
                                    opacity: isAtRootFolder ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isAtRootFolder) {
                                      setSelectedItems(new Set([item.path]));
                                      setRenameItemPath(
                                        item.path.split("\\").pop() || ""
                                      );

                                      const itemName =
                                        item.path.split("\\").pop() || "";
                                      const nameWithoutExtension =
                                        itemName.includes(".")
                                          ? itemName.substring(
                                              0,
                                              itemName.lastIndexOf(".")
                                            )
                                          : itemName;

                                      setRenameItemPath(nameWithoutExtension);
                                    }
                                  }}
                                />
                              </span>
                            </Tooltip>

                            {/* <Tooltip
                              title={
                                isAtRootFolder
                                  ? "Ação não permitida na Área de Trabalho."
                                  : "Copiar"
                              }
                              arrow
                            >
                              <span
                                style={{
                                  cursor: isAtRootFolder
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                <ContentCopyRoundedIcon
                                  sx={{
                                    color:
                                      mode === "dark"
                                        ? darkTextColor
                                        : menuUserDarkTheme,
                                    "&:hover": {
                                      color: isAtRootFolder
                                        ? "inherit"
                                        : "#1976d2",
                                    },
                                    pointerEvents: isAtRootFolder
                                      ? "none"
                                      : "auto",
                                    opacity: isAtRootFolder ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isAtRootFolder) {
                                      const updatedSelection = new Set([
                                        item.path,
                                      ]);
                                      setSelectedItems(updatedSelection);
                                      setTimeout(() => {
                                        document.dispatchEvent(
                                          new CustomEvent("triggerCopy")
                                        );
                                      }, 0);
                                    }
                                  }}
                                />
                              </span>
                            </Tooltip> */}
                            <Tooltip
                              title={
                                isAtRootFolder
                                  ? "Ação não permitida na Área de Trabalho."
                                  : "Mover"
                              }
                              arrow
                            >
                              <span
                                style={{
                                  cursor: isAtRootFolder
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                <ContentCutRoundedIcon
                                  sx={{
                                    color:
                                      mode === "dark"
                                        ? darkTextColor
                                        : menuUserDarkTheme,
                                    "&:hover": {
                                      color: isAtRootFolder
                                        ? "inherit"
                                        : "#1976d2",
                                    },
                                    pointerEvents: isAtRootFolder
                                      ? "none"
                                      : "auto",
                                    opacity: isAtRootFolder ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isAtRootFolder) {
                                      const updatedSelection = new Set([
                                        item.path,
                                      ]);
                                      setSelectedItems(updatedSelection);
                                      setTimeout(() => {
                                        document.dispatchEvent(
                                          new CustomEvent("triggerMove")
                                        );
                                      }, 0);
                                    }
                                  }}
                                />
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                isAtRootFolder
                                  ? "Ação não permitida na Área de Trabalho."
                                  : "Excluir"
                              }
                              arrow
                            >
                              <span
                                style={{
                                  cursor: isAtRootFolder
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                <DeleteForeverIcon
                                  sx={{
                                    color:
                                      mode === "dark"
                                        ? darkTextColor
                                        : menuUserDarkTheme,
                                    "&:hover": {
                                      color: isAtRootFolder
                                        ? "inherit"
                                        : "#d32f2f",
                                    },
                                    pointerEvents: isAtRootFolder
                                      ? "none"
                                      : "auto",
                                    opacity: isAtRootFolder ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isAtRootFolder) {
                                      const updatedSelection = new Set([
                                        item.path,
                                      ]);
                                      setSelectedItems(updatedSelection);
                                      setTimeout(() => {
                                        document.dispatchEvent(
                                          new CustomEvent("triggerDelete")
                                        );
                                      }, 0);
                                    }
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </Box>
                        </Grid>
                      </Grid>
                    </ListItem>
                  );
                })}
                {items.length === 0 && (
                  <Typography sx={disabledTextStyle}>
                    Nenhuma pasta ou arquivo encontrado.
                  </Typography>
                )}
              </List>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  position: "relative",
                  bottom: 0,
                  width: "100%",
                  borderTop: "1px solid",
                  borderColor: theme.palette.text.disabled,
                  mt: "auto",
                  padding: "12px", // Aumenta o espaço interno
                  height: "30px", // Define uma altura maior para o box
                  boxSizing: "border-box", // Garante que o padding e o height se combinem corretamente
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "14px" }}>
                  {statusMessage &&
                    `${statusMessage}${hoverStatusMessage ? " | " : ""}`}{" "}
                  {/* Adiciona a barra somente se hoverStatusMessage existir */}
                  {hoverStatusMessage && `${hoverStatusMessage}`}{" "}
                  {/* Adiciona a mensagem de hover, se existir */}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default User;
